import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button as ChakraButton,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  Textarea as ChakraTextarea,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { MarketDataConnectGateNotice } from "@/components/MarketData/MarketDataConnectGateNotice";
import { MarketDataUnavailablePanel } from "@/components/MarketData/MarketDataUnavailablePanel";
import type { MarketDataConnectGateState } from "@/utils/marketDataConnectGate";
import type { MarketDataUnavailablePayload } from "@/types/marketDataUnavailable";
import { MARKET_DATA_CONNECT_GATE_BLOCKED_MESSAGE } from "@/utils/marketDataConnectGate";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { DashboardHeaderDateRangePicker } from "../Dashboard/components/DashboardHeaderDateRangePicker";
import { useConfiguratorStyleTextareaProps } from "../../../components/Reports/ConfiguratorStyleMenu";
import { DateRange } from "../../../types/trade";
import type {
  DashboardDatePresetTimeframe,
  DashboardDateRangeChangeOptions,
} from "@/utils/dashboardDateWindowStorage";
import type { ReportStrategyResponse, ReportTypeOption } from "../../../types/reports";
import type { AvailableInstrumentRow } from "../../../types/tradeManagementReport";
import type { CandleTimeframePreviewUi } from "../../../utils/candleTimeframePreview";
import { SimulatorCandleBudgetGate } from "../ExecutionRulesSimulator/SimulatorCandleBudgetGate";
import { SimulatorAlert } from "../ExecutionRulesSimulator/SimulatorAlert";
import type { AnalyticsWindowSnakeFields } from "../../../utils/analyticsWindowParams";
import { needsMarketDataSourceChoiceForRow, selectedInstrumentOhlcSubline } from "../../../utils/marketDataSourceChoice";
import {
  buildReportInstrumentFieldDescription,
  buildReportInstrumentSelectOptions,
  userFacingInstrumentsSelectionHint,
} from "../../../utils/reportInstrumentSelection";
import { REPORT_TIMEFRAME_FIELD_HELP, reportCandleBudgetBlockedMessage } from "../../../utils/reportTimeframeCopy";
import {
  MarketContextBarCoverageContent,
  MarketContextBarCoverageHeading,
  MarketContextBarCoverageTrigger,
} from "./components/MarketContextBarCoverageDialog";
import { TradeFilterSelectFullWidth } from "@/components/trade-filter-selects";
import { PerformanceReviewSymbolsField } from "./components/PerformanceReviewSymbolsField";
import { resolveGenerateReportSteps } from "./generateReportConstants";
import {
  GenerateReportWizardStepper,
  GenerateReportWizardStepperContent,
} from "./GenerateReportWizardStepper";
import { reportPageFieldFocusSx } from "./reportPageFieldFocus";
import { useWorkspaceWizardFooterSlot } from "../Workspace/WorkspaceWizardDialog";

const DIALOG_PANEL_TRANSITION = { duration: 0.34, ease: [0.22, 1, 0.36, 1] as const };

// Chakra polymorphic props can exceed TS union depth in large wizard TSX files.
const AlertAny = Alert as any;
const AlertIconAny = AlertIcon as any;
const AlertDescriptionAny = AlertDescription as any;
const SimpleGridAny = SimpleGrid as any;
const VStackAny = VStack as any;
const TextAny = Text as any;
const BoxAny = Box as any;
const FlexAny = Flex as any;
const HStackAny = HStack as any;
const ChakraButtonAny = ChakraButton as any;
const ChakraTextareaAny = ChakraTextarea as unknown as React.FC<any>;

function GenerateReportWizardDialogBody(props: {
  accent: string;
  barCoverageOpen: boolean;
  reduceMotion: boolean;
  panelTransition: typeof DIALOG_PANEL_TRANSITION | { duration: number };
  hasInflightReport: boolean;
  weeklyReportQuotaReached?: boolean;
  weeklyReportQuotaMessage?: string | null;
  stepperAndSteps: React.ReactNode;
}): React.ReactElement {
  // Wizard exits left / enters from left; bar coverage exits right / enters from right.
  const wizardSlide = props.reduceMotion
    ? undefined
    : { initial: { x: "-100%" }, exit: { x: "-100%" } };
  const coverageSlide = props.reduceMotion
    ? undefined
    : { initial: { x: "100%" }, exit: { x: "100%" } };

  return (
    <div
      data-reports-wizard-dialog=""
      data-generate-report-wizard-layout="dialog"
      className="relative flex w-full flex-col"
      style={{ ["--generate-report-accent" as string]: props.accent }}
    >
      <div className="relative w-full overflow-hidden">
        <AnimatePresence initial={false} mode="sync">
          {props.barCoverageOpen ? (
            <motion.div
              key="bar-coverage"
              className="flex w-full flex-col"
              initial={coverageSlide?.initial ?? false}
              animate={{ x: 0 }}
              exit={
                coverageSlide
                  ? { ...coverageSlide.exit, position: "absolute", insetInline: 0, top: 0 }
                  : undefined
              }
              transition={props.panelTransition}
            >
              <div className="px-6 py-4">
                <div className="mb-4">
                  <MarketContextBarCoverageHeading />
                </div>
                <MarketContextBarCoverageContent />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="wizard-steps"
              className="flex w-full flex-col"
              initial={wizardSlide?.initial ?? false}
              animate={{ x: 0 }}
              exit={
                wizardSlide
                  ? { ...wizardSlide.exit, position: "absolute", insetInline: 0, top: 0 }
                  : undefined
              }
              transition={props.panelTransition}
            >
              {props.hasInflightReport ? (
                <div className="shrink-0 px-6 pt-4">
                  <AlertAny status="warning" borderRadius="12px">
                    <AlertIconAny />
                    <AlertDescriptionAny fontSize="sm">
                      A report is already generating. Wait for it to finish before starting another.
                    </AlertDescriptionAny>
                  </AlertAny>
                </div>
              ) : props.weeklyReportQuotaReached ? (
                <div className="shrink-0 px-6 pt-4">
                  <AlertAny status="warning" borderRadius="12px">
                    <AlertIconAny />
                    <AlertDescriptionAny fontSize="sm">
                      {props.weeklyReportQuotaMessage ||
                        "You've reached this week's report limit. Wait until next week, or upgrade to Plus for a higher cap."}
                    </AlertDescriptionAny>
                  </AlertAny>
                </div>
              ) : null}
              <div className="px-6 py-4">{props.stepperAndSteps}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export type GenerateReportTradeSideFilter = "both" | "long" | "short";
export type GenerateReportStrategyMode = "none" | "existing" | "new";

const GENERATE_REPORT_WIZARD_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

const GENERATE_REPORT_WIZARD_FIELD_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 whitespace-normal md:min-h-[2.5rem]";

function tradeSideFilterLabel(value: GenerateReportTradeSideFilter): string {
  if (value === "long") return "Long";
  if (value === "short") return "Short";
  return "Both";
}

function GenerateReportWizardFieldBlock(props: {
  label: string;
  description: React.ReactNode;
  descriptionAction?: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Field
      className={`w-full min-w-0 content-start [grid-template-rows:auto_minmax(2.25rem,auto)_auto]${
        props.className ? ` ${props.className}` : ""
      }`}
      data-generate-report-wizard-fields=""
    >
      <FieldLabel htmlFor={props.htmlFor} className={`${GENERATE_REPORT_WIZARD_FIELD_LABEL_CLASS} min-h-5 leading-5`}>
        {props.label}
        {props.required ? (
          <span className="ml-0.5 text-[#E00101]" aria-hidden>
            *
          </span>
        ) : null}
      </FieldLabel>
      <div className="min-h-9 w-full min-w-0 flex flex-col items-stretch gap-1">{props.children}</div>
      <FieldDescription className={GENERATE_REPORT_WIZARD_FIELD_DESC_CLASS}>
        {props.description}
      </FieldDescription>
      {props.descriptionAction ? (
        <div className="mt-1.5 w-full self-start text-left">{props.descriptionAction}</div>
      ) : null}
    </Field>
  );
}

const GENERATE_REPORT_ACCENT = "#38BBD1";

function GenerateReportWizardSelect(props: {
  value: string;
  onValueChange: (value: string) => void;
  options: { value: string; label: string; disabled?: boolean }[];
  disabled?: boolean;
  placeholder?: string;
}): React.ReactElement {
  return (
    <div data-generate-report-wizard-fields="" className="w-full min-w-0">
      <TradeFilterSelectFullWidth
        value={props.value}
        onValueChange={props.onValueChange}
        options={props.options}
        disabled={props.disabled}
        placeholder={props.placeholder}
        emptyValueShowsPlaceholder
      />
    </div>
  );
}

export type GenerateReportWizardProps = {
  /** `page` = full /reports/new chrome; `dialog` = stepped body for workspace (and similar) dialogs. */
  layout?: "page" | "dialog";
  /** When set, remount/reset consumers should change this to return to step 0 (e.g. dialog open). */
  resetToken?: string | number | boolean;
  /** Lifted step index — survives parent remounts (e.g. instrument-setup gate bypass toggles). */
  activeStepIndex?: number;
  onActiveStepIndexChange?: (index: number) => void;
  /** Disable broker account picker (workspace canvas already scopes the account). */
  lockBrokerAccount?: boolean;
  /**
   * Hide the Strategy step (e.g. workspace create-report under an existing strategy node).
   * Strategy attachment still comes from `strategyMode` / `selectedStrategyId`.
   */
  omitStrategyStep?: boolean;
  /** Override the Strategy step description (workspace account-level create). */
  strategyStepDescription?: string;
  /** Dialog host — notified when the in-dialog bar-coverage panel opens/closes. */
  onBarCoverageOpenChange?: (open: boolean) => void;
  subtleText: string;
  textColor: string;
  borderColor: string;
  effectiveBrokerAccountCode: string;
  brokerAccountOptions: { value: string; label: string }[];
  brokerAccountsLoading: boolean;
  onBrokerAccountChange: (accountCode: string) => void;
  tradeSideFilter: GenerateReportTradeSideFilter;
  onTradeSideFilterChange: (value: GenerateReportTradeSideFilter) => void;
  showMarketContextFields: boolean;
  showPerformanceReviewSymbolFields: boolean;
  symbolRequired: boolean;
  symbol: string;
  performanceReviewAllSymbols: boolean;
  onPerformanceReviewAllSymbolsChange: (allSymbols: boolean) => void;
  performanceReviewSymbols: string[];
  onPerformanceReviewSymbolsChange: (symbols: string[]) => void;
  onSymbolChange: (value: string) => void;
  onSymbolBlur: (symbolOverride?: string) => void;
  instrumentResolveMessage: string | null;
  mdBlocked: boolean;
  marketDataConnectGate?: MarketDataConnectGateState | null;
  marketDataUnavailable?: MarketDataUnavailablePayload | null;
  onMarketDataUnavailablePickCandleType?: (timeframe: string) => void;
  onMarketDataUnavailableChangeSource?: () => void;
  onMarketDataUnavailableConnectBroker?: (broker: string) => void;
  onMarketDataConnectNavigate?: (href: string) => void;
  marketDataAvailabilityChecking?: boolean;
  /** Jump to instrument/review step after market-data connect gate (dialog remount). */
  resumeStepKey?: string | null;
  marketDataSourceChoicePending?: boolean;
  instrumentNeedsOhlcSourceChoice?: boolean;
  instrumentMarketDataSetupRecommended?: boolean;
  onConfigureInstrumentMarketData?: () => void;
  selectedInstrumentRow?: AvailableInstrumentRow;
  candleTimeframePreviewUi?: CandleTimeframePreviewUi | null;
  candleTimeframePreviewLoading?: boolean;
  /** Show shared ~60k bar budget gate (Exit Placement / Entry Analysis). */
  showCandleBudgetGate?: boolean;
  candleBudgetExceeds?: boolean;
  candleBudgetWindowFields?: AnalyticsWindowSnakeFields;
  onSelectCandleBudgetPeriod?: (startYmd: string, endYmd: string) => void;
  onCandleBudgetExceedsChange?: (exceeds: boolean) => void;
  availableInstruments: AvailableInstrumentRow[];
  instrumentsSelectionHint?: string | null;
  instrumentsLoading: boolean;
  onReloadAvailableInstruments: () => void | Promise<void>;
  dateRange: DateRange;
  selectedPresetId?: DashboardDatePresetTimeframe | null;
  onDateRangeChange: (range: DateRange, options?: DashboardDateRangeChangeOptions) => void;
  reportTitle: string;
  onReportTitleChange: (value: string) => void;
  reportTitleMax: number;
  selectedReportType: string;
  onReportTypeChange: (value: string) => void;
  reportTypeOptions: ReportTypeOption[];
  reportTypesLoading: boolean;
  onReloadReportTypes: () => void | Promise<void>;
  onReloadStrategies: () => void | Promise<void>;
  selectedReportTypeDescription: string;
  candleTimeframe: "" | "1m" | "5m" | "15m" | "30m" | "1h" | "4h";
  onCandleTimeframeChange: (value: "" | "1m" | "5m" | "15m" | "30m" | "1h" | "4h") => void;
  strategyMode: GenerateReportStrategyMode;
  onStrategyModeChange: (mode: GenerateReportStrategyMode) => void;
  strategyOptions: ReportStrategyResponse[];
  selectedStrategyId: string;
  onSelectedStrategyIdChange: (id: string) => void;
  strategiesLoading: boolean;
  /** Canvas prefill title when the strategy id is known before options load. */
  prefillStrategyName?: string | null;
  strategyName: string;
  onStrategyNameChange: (value: string) => void;
  strategyNameLimit: number;
  strategyDescription: string;
  onStrategyDescriptionChange: (value: string) => void;
  strategyDescriptionLimit: number;
  onEditStrategy: () => void;
  canGenerate: boolean;
  /** When generate is blocked, human-readable reason for the footer alert. */
  generateBlockedReason?: string | null;
  generating: boolean;
  onGenerate: () => void;
  onCancel: () => void;
  hasInflightReport: boolean;
  weeklyReportQuotaReached?: boolean;
  weeklyReportQuotaMessage?: string | null;
  inflightCheckLoading?: boolean;
  rateLimitCoolingDown?: boolean;
  rateLimitRetryAfterSeconds?: number;
  windowSummaryLabel: string;
};

export function GenerateReportWizard(props: GenerateReportWizardProps): React.ReactElement {
  const layout = props.layout ?? "page";
  const isDialog = layout === "dialog";
  const [internalStepIndex, setInternalStepIndex] = useState(0);
  const stepControlled =
    props.activeStepIndex !== undefined && props.onActiveStepIndexChange !== undefined;
  const activeStepIndex = stepControlled ? props.activeStepIndex! : internalStepIndex;
  const setActiveStepIndex = stepControlled
    ? props.onActiveStepIndexChange!
    : setInternalStepIndex;
  const [barCoverageOpen, setBarCoverageOpen] = useState(false);
  /** Avoid refetching the picker when Continue just returns to the same cohort. */
  const lastInstrumentsCohortKeyRef = useRef<string | null>(null);
  const reduceMotion = useReducedMotion();
  const textareaProps = useConfiguratorStyleTextareaProps();
  const accent = GENERATE_REPORT_ACCENT;
  const menuRadius = "6px";

  const visibleSteps = useMemo(
    () =>
      resolveGenerateReportSteps({
        omitStrategyStep: props.omitStrategyStep,
        strategyStepDescription: props.strategyStepDescription,
      }),
    [props.omitStrategyStep, props.strategyStepDescription]
  );
  const lastStepIndex = Math.max(0, visibleSteps.length - 1);
  const activeStepKey = visibleSteps[activeStepIndex]?.key ?? "trade_scope";

  useEffect(() => {
    if (props.resetToken === undefined) return;
    setActiveStepIndex(0);
    setBarCoverageOpen(false);
    lastInstrumentsCohortKeyRef.current = null;
  }, [props.resetToken, setActiveStepIndex]);

  useEffect(() => {
    if (!props.resumeStepKey) return;
    const index = visibleSteps.findIndex((step) => step.key === props.resumeStepKey);
    if (index >= 0) setActiveStepIndex(index);
  }, [props.resumeStepKey, visibleSteps]);

  useEffect(() => {
    if (activeStepIndex > lastStepIndex) {
      setActiveStepIndex(lastStepIndex);
    }
  }, [activeStepIndex, lastStepIndex, setActiveStepIndex]);

  useEffect(() => {
    props.onBarCoverageOpenChange?.(barCoverageOpen);
  }, [barCoverageOpen, props.onBarCoverageOpenChange]);

  const openBarCoverage = useCallback(() => setBarCoverageOpen(true), []);
  const closeBarCoverage = useCallback(() => setBarCoverageOpen(false), []);

  const cardBg = useColorModeValue("white", "var(--sidebar)");
  const cardShadow = useColorModeValue(
    "0 20px 40px rgba(15, 23, 42, 0.06)",
    "none"
  );

  const tradeSideLabel = tradeSideFilterLabel(props.tradeSideFilter);

  const strategySummary = useMemo(() => {
    if (props.strategyMode === "none") return "No strategy attached";
    if (props.strategyMode === "existing") {
      const usable = (raw: string | null | undefined) => {
        const t = raw?.trim() || "";
        if (!t || t.toLowerCase() === "strategy") return "";
        return t;
      };
      const fromOptions = usable(
        props.strategyOptions.find((s) => s.id === props.selectedStrategyId)?.name
      );
      const fromPrefill = usable(props.prefillStrategyName);
      const name = fromPrefill || fromOptions;
      const isPrefill =
        Boolean(props.omitStrategyStep) || Boolean(props.prefillStrategyName?.trim()) || Boolean(fromPrefill);
      if (name) {
        return isPrefill || Boolean(props.omitStrategyStep)
          ? `Using “${name}” (preselected)`
          : `Using “${name}”`;
      }
      if (props.selectedStrategyId?.trim() && (isPrefill || props.omitStrategyStep)) {
        return "Using strategy (preselected)";
      }
      if (props.selectedStrategyId?.trim()) return "Strategy selected";
      return "Existing strategy — choose one";
    }
    const name = props.strategyName.trim();
    return name ? `New strategy: “${name}”` : "New strategy (name optional)";
  }, [
    props.strategyMode,
    props.strategyName,
    props.selectedStrategyId,
    props.strategyOptions,
    props.prefillStrategyName,
    props.omitStrategyStep,
  ]);

  const instrumentOptions = useMemo(
    () =>
      props.showMarketContextFields
        ? buildReportInstrumentSelectOptions(props.availableInstruments)
        : props.availableInstruments.map((row) => ({
            value: row.symbol,
            label: row.symbol,
          })),
    [props.availableInstruments, props.showMarketContextFields]
  );

  const showSymbolScopeFields =
    props.showMarketContextFields || props.showPerformanceReviewSymbolFields;

  const instrumentsInitialLoading =
    props.instrumentsLoading && props.availableInstruments.length === 0;

  const performanceReviewSymbolDescription = useMemo(() => {
    if (instrumentsInitialLoading) {
      return "Loading symbols for your account and date range…";
    }
    const friendly = userFacingInstrumentsSelectionHint(props.instrumentsSelectionHint);
    if (friendly) return friendly;
    if (props.availableInstruments.length === 0) {
      return "No symbols with trades in this account and date range.";
    }
    return "Include every symbol traded in this window, or limit the report to specific symbols.";
  }, [
    instrumentsInitialLoading,
    props.availableInstruments.length,
    props.instrumentsSelectionHint,
  ]);

  const instrumentFieldDescription = useMemo(
    () =>
      props.showMarketContextFields
        ? buildReportInstrumentFieldDescription({
            instruments: props.availableInstruments,
            instrumentsLoading: instrumentsInitialLoading,
            symbolRequired: props.symbolRequired,
            backendHint: props.instrumentsSelectionHint,
          })
        : "",
    [
      props.availableInstruments,
      instrumentsInitialLoading,
      props.instrumentsSelectionHint,
      props.showMarketContextFields,
      props.symbolRequired,
    ]
  );

  const symbolScopeFieldLabel = props.showMarketContextFields ? "Instrument" : "Symbols";
  const symbolScopeFieldDescription = props.showMarketContextFields
    ? instrumentFieldDescription
    : performanceReviewSymbolDescription;

  const instrumentOhlcSubline = useMemo(
    () => selectedInstrumentOhlcSubline(props.selectedInstrumentRow),
    [props.selectedInstrumentRow]
  );

  useEffect(() => {
    if (activeStepKey !== "report_details" || !showSymbolScopeFields) return;
    const cohortKey = JSON.stringify({
      account: props.effectiveBrokerAccountCode,
      side: props.tradeSideFilter,
      start: props.dateRange.startDate?.toISOString() ?? null,
      end: props.dateRange.endDate?.toISOString() ?? null,
    });
    if (lastInstrumentsCohortKeyRef.current === cohortKey) return;
    lastInstrumentsCohortKeyRef.current = cohortKey;
    void props.onReloadAvailableInstruments();
  }, [
    activeStepKey,
    showSymbolScopeFields,
    props.tradeSideFilter,
    props.effectiveBrokerAccountCode,
    props.dateRange.startDate,
    props.dateRange.endDate,
    props.onReloadAvailableInstruments,
  ]);

  const handleInstrumentChange = useCallback(
    (value: string) => {
      const row = props.availableInstruments.find((r) => r.symbol === value);
      props.onSymbolChange(value);
      if (row && props.instrumentMarketDataSetupRecommended) return;
      if (row && needsMarketDataSourceChoiceForRow(row)) return;
      if (value.trim()) {
        props.onSymbolBlur(value);
      }
    },
    [
      props.availableInstruments,
      props.instrumentMarketDataSetupRecommended,
      props.onSymbolChange,
      props.onSymbolBlur,
    ]
  );

  const stepValidationMessage = useMemo(() => {
    if (activeStepKey === "trade_scope") {
      const hasExplicitRange = Boolean(props.dateRange.startDate && props.dateRange.endDate);
      const hasAllTime = !props.dateRange.startDate && !props.dateRange.endDate;
      if (!hasExplicitRange && !hasAllTime) {
        return "Choose a date range or use All Time.";
      }
    }
    if (activeStepKey === "report_details") {
      if (!props.reportTypeOptions.some((o) => o.key === props.selectedReportType)) {
        return "Report types are still loading or the selected type is invalid.";
      }
      if (!props.candleTimeframe) return "Choose a candle timeframe to continue.";
      if (props.showMarketContextFields && props.symbolRequired && !props.symbol.trim()) {
        return "Choose an instrument for this report type.";
      }
      if (
        props.showPerformanceReviewSymbolFields &&
        !props.performanceReviewAllSymbols &&
        props.performanceReviewSymbols.length === 0
      ) {
        return "Choose at least one symbol, or include all symbols.";
      }
      if (props.marketDataConnectGate) {
        return MARKET_DATA_CONNECT_GATE_BLOCKED_MESSAGE;
      }
      if (props.mdBlocked) return "Market data is not available for the selected instrument.";
      if (props.marketDataSourceChoicePending) {
        return "Choose an OHLC listing for this symbol.";
      }
      if (
        props.showMarketContextFields &&
        props.symbol.trim() &&
        props.candleTimeframe &&
        props.candleTimeframePreviewLoading
      ) {
        return "Checking candle timeframe availability for your date range…";
      }
      if (props.candleBudgetExceeds || props.candleTimeframePreviewUi?.exceedsBudget) {
        return reportCandleBudgetBlockedMessage(props.candleTimeframe || "This timeframe");
      }
      if (props.marketDataUnavailable) {
        return (
          props.marketDataUnavailable.message.trim() ||
          "Chart data is unavailable for this symbol and timeframe."
        );
      }
      if (props.marketDataAvailabilityChecking) {
        return "Checking market data connection for your instrument…";
      }
    }
    if (activeStepKey === "generate") {
      if (props.marketDataAvailabilityChecking) {
        return "Checking market data connection for your instrument…";
      }
      if (props.marketDataConnectGate) {
        return MARKET_DATA_CONNECT_GATE_BLOCKED_MESSAGE;
      }
    }
    return null;
  }, [
    activeStepKey,
    props.dateRange.endDate,
    props.dateRange.startDate,
    props.selectedReportType,
    props.reportTypeOptions,
    props.candleTimeframe,
    props.candleTimeframePreviewUi,
    props.candleBudgetExceeds,
    props.showMarketContextFields,
    props.showPerformanceReviewSymbolFields,
    props.performanceReviewAllSymbols,
    props.performanceReviewSymbols,
    props.symbolRequired,
    props.symbol,
    props.marketDataConnectGate,
    props.marketDataAvailabilityChecking,
    props.instrumentMarketDataSetupRecommended,
    props.mdBlocked,
    props.marketDataSourceChoicePending,
    props.marketDataUnavailable,
    props.candleTimeframePreviewLoading,
  ]);

  const reportNameMissing = activeStepKey === "report_details" && !props.reportTitle.trim();
  const canAdvance = activeStepIndex < lastStepIndex && !stepValidationMessage && !reportNameMissing;

  const footerAlertMessage = useMemo(() => {
    if (barCoverageOpen) return null;
    if (reportNameMissing) return "Enter a report name to continue.";
    if (props.candleBudgetExceeds || props.candleTimeframePreviewUi?.exceedsBudget) {
      return reportCandleBudgetBlockedMessage(props.candleTimeframe || "This timeframe");
    }
    if (stepValidationMessage) return stepValidationMessage;
    if (activeStepIndex === lastStepIndex && !props.canGenerate) {
      return props.generateBlockedReason?.trim() || null;
    }
    return null;
  }, [
    activeStepIndex,
    barCoverageOpen,
    lastStepIndex,
    props.canGenerate,
    props.candleBudgetExceeds,
    props.candleTimeframe,
    props.candleTimeframePreviewUi?.exceedsBudget,
    props.generateBlockedReason,
    reportNameMissing,
    stepValidationMessage,
  ]);

  const goNext = useCallback(() => {
    if (stepValidationMessage || reportNameMissing) return;
    const next = Math.min(lastStepIndex, activeStepIndex + 1);
    const nextKey = visibleSteps[next]?.key;
    setActiveStepIndex(next);
    if (nextKey === "report_details") {
      void props.onReloadReportTypes();
    } else if (nextKey === "strategy") {
      void props.onReloadStrategies();
    }
  }, [
    activeStepIndex,
    lastStepIndex,
    visibleSteps,
    stepValidationMessage,
    reportNameMissing,
    props.onReloadReportTypes,
    props.onReloadStrategies,
    setActiveStepIndex,
  ]);

  const goBack = useCallback(() => {
    setActiveStepIndex(Math.max(0, activeStepIndex - 1));
  }, [activeStepIndex, setActiveStepIndex]);

  const reportTypeLabel =
    props.reportTypeOptions.find((o) => o.key === props.selectedReportType)?.label ?? props.selectedReportType;

  const instrumentReviewLabel = props.showMarketContextFields
    ? props.symbol.trim() || (props.symbolRequired ? "Not selected" : "—")
    : props.showPerformanceReviewSymbolFields
      ? props.performanceReviewAllSymbols
        ? "All symbols"
        : props.performanceReviewSymbols.length === 0
          ? "Not selected"
          : props.performanceReviewSymbols.length <= 4
            ? props.performanceReviewSymbols.join(", ")
            : `${props.performanceReviewSymbols.length} symbols`
      : "—";

  const symbolScopeReviewLabel = props.showMarketContextFields ? "Instrument" : "Symbols";

  const stepForm = (
    <>
      {activeStepKey === "trade_scope" ? (
        <SimpleGridAny
          w="100%"
          columns={isDialog ? { base: 1, sm: 2 } : { base: 1, md: 2, lg: 3 }}
          spacing={isDialog ? 5 : 8}
        >
          <GenerateReportWizardFieldBlock
            label="Broker account"
            description={
              props.lockBrokerAccount
                ? "This report uses the workspace account."
                : "Choose which trade account this report should use."
            }
          >
            {props.lockBrokerAccount ? (
              <Input
                readOnly
                disabled
                value={
                  props.brokerAccountOptions.find(
                    (opt) => opt.value === props.effectiveBrokerAccountCode
                  )?.label ||
                  props.effectiveBrokerAccountCode ||
                  "—"
                }
                className="w-full"
              />
            ) : (
              <GenerateReportWizardSelect
                value={props.effectiveBrokerAccountCode}
                onValueChange={props.onBrokerAccountChange}
                options={props.brokerAccountOptions}
                disabled={
                  props.brokerAccountsLoading ||
                  props.brokerAccountOptions.length === 0
                }
                placeholder={
                  props.brokerAccountsLoading
                    ? "Loading accounts…"
                    : props.brokerAccountOptions.length === 0
                      ? "No trade accounts"
                      : "Select account"
                }
              />
            )}
          </GenerateReportWizardFieldBlock>
          <GenerateReportWizardFieldBlock
            label="Trade side"
            description="Include longs, shorts, or both in this report."
          >
            <GenerateReportWizardSelect
              value={props.tradeSideFilter}
              onValueChange={(v) => props.onTradeSideFilterChange(v as GenerateReportTradeSideFilter)}
              options={[
                { value: "both", label: "Both" },
                { value: "long", label: "Long" },
                { value: "short", label: "Short" },
              ]}
            />
          </GenerateReportWizardFieldBlock>
          <GenerateReportWizardFieldBlock
            label="Date range"
            description="Please choose your specified date range."
          >
            <div data-generate-report-date-range="" className="w-full min-w-0">
              <DashboardHeaderDateRangePicker
                dateRange={props.dateRange}
                selectedPresetId={props.selectedPresetId}
                onDateRangeChange={props.onDateRangeChange}
                className="w-full"
                triggerSurface="field"
              />
            </div>
          </GenerateReportWizardFieldBlock>
        </SimpleGridAny>
      ) : null}

      {activeStepKey === "report_details" ? (
        <SimpleGridAny
          w="100%"
          columns={
            isDialog
              ? { base: 1, sm: 2 }
              : {
                  base: 1,
                  md: showSymbolScopeFields ? 2 : 3,
                  lg: showSymbolScopeFields ? 4 : 3,
                }
          }
          spacing={isDialog ? 5 : 8}
        >
          <GenerateReportWizardFieldBlock
            label="Report name"
            description="Choose a name for your report."
            required
          >
            <div data-trade-filter-bar="" data-trade-filter-bar-layout="full" className="w-full min-w-0">
              <Input
                value={props.reportTitle}
                onChange={(e) => props.onReportTitleChange(e.target.value.slice(0, props.reportTitleMax))}
                placeholder={`Required (max ${props.reportTitleMax} characters)`}
                maxLength={props.reportTitleMax}
                required
                aria-required
                className="text-gray-500"
              />
            </div>
          </GenerateReportWizardFieldBlock>

          <GenerateReportWizardFieldBlock
            label="Report type"
            description={
              props.selectedReportType
                ? (props.selectedReportTypeDescription || "Select the analysis template for this report.")
                : "Choose your report type."
            }
            required
          >
            <GenerateReportWizardSelect
              value={props.selectedReportType}
              onValueChange={props.onReportTypeChange}
              disabled={props.reportTypesLoading || props.reportTypeOptions.length === 0}
              placeholder={
                props.reportTypesLoading
                  ? "Loading report types…"
                  : props.reportTypeOptions.length === 0
                    ? "No report types"
                    : "Choose report type"
              }
              options={props.reportTypeOptions.map((opt) => ({
                value: opt.key,
                label: opt.label,
              }))}
            />
          </GenerateReportWizardFieldBlock>

          {showSymbolScopeFields ? (
            <GenerateReportWizardFieldBlock
              label={symbolScopeFieldLabel}
              description={symbolScopeFieldDescription}
              required={props.showMarketContextFields ? props.symbolRequired : false}
            >
              {props.showMarketContextFields ? (
                <>
                  <GenerateReportWizardSelect
                    value={props.symbol}
                    onValueChange={handleInstrumentChange}
                    disabled={!props.effectiveBrokerAccountCode || instrumentsInitialLoading}
                    placeholder={
                      instrumentsInitialLoading
                        ? "Loading instruments…"
                        : instrumentOptions.length === 0
                          ? "No instruments in scope"
                          : "Choose instrument"
                    }
                    options={instrumentOptions}
                  />
                  {instrumentOhlcSubline ? (
                    <TextAny mt={1} fontSize="xs" color={props.subtleText} lineHeight="tall">
                      {instrumentOhlcSubline}
                    </TextAny>
                  ) : null}
                  {props.instrumentResolveMessage ? (
                    <TextAny mt={1} fontSize="xs" color="red.500" role="alert">
                      {props.instrumentResolveMessage}
                    </TextAny>
                  ) : null}
                  {props.marketDataConnectGate && props.onMarketDataConnectNavigate ? (
                    <BoxAny mt={3} w="full">
                      <MarketDataConnectGateNotice
                        message={props.marketDataConnectGate.message}
                        action={props.marketDataConnectGate.action}
                        tradeAnalyticsAvailable={
                          props.marketDataConnectGate.trade_analytics_available
                        }
                        onNavigate={props.onMarketDataConnectNavigate}
                      />
                    </BoxAny>
                  ) : null}
                </>
              ) : (
                <PerformanceReviewSymbolsField
                  allSymbols={props.performanceReviewAllSymbols}
                  onAllSymbolsChange={props.onPerformanceReviewAllSymbolsChange}
                  selectedSymbols={props.performanceReviewSymbols}
                  onSelectedSymbolsChange={props.onPerformanceReviewSymbolsChange}
                  instruments={props.availableInstruments}
                  instrumentsLoading={instrumentsInitialLoading}
                  disabled={!props.effectiveBrokerAccountCode}
                />
              )}
            </GenerateReportWizardFieldBlock>
          ) : null}

          <GenerateReportWizardFieldBlock
            label="Timeframe"
            description={REPORT_TIMEFRAME_FIELD_HELP}
            descriptionAction={
              <MarketContextBarCoverageTrigger
                onOpen={isDialog ? openBarCoverage : undefined}
              />
            }
            required
            className={
              isDialog && !showSymbolScopeFields ? "[grid-column:1/-1]" : undefined
            }
          >
            <GenerateReportWizardSelect
              value={props.candleTimeframe}
              onValueChange={(v) => props.onCandleTimeframeChange(v as GenerateReportWizardProps["candleTimeframe"])}
              placeholder="Choose timeframe"
              options={[
                { value: "1m", label: "1m" },
                { value: "5m", label: "5m" },
                { value: "15m", label: "15m" },
                { value: "30m", label: "30m" },
                { value: "1h", label: "1h" },
                { value: "4h", label: "4h" },
              ]}
            />
            {props.showCandleBudgetGate &&
            props.candleBudgetWindowFields &&
            props.onSelectCandleBudgetPeriod ? (
              <div className="hidden" aria-hidden>
                <SimulatorCandleBudgetGate
                  candleTimeframe={props.candleTimeframe || "1h"}
                  windowFields={props.candleBudgetWindowFields}
                  symbol={props.symbol.trim() || undefined}
                  brokerAccountCode={props.effectiveBrokerAccountCode || null}
                  candleTimeframeLocked
                  silent
                  activeLocalStart={props.candleBudgetWindowFields.local_start_date ?? null}
                  activeLocalEnd={props.candleBudgetWindowFields.local_end_date ?? null}
                  onSelectPeriod={props.onSelectCandleBudgetPeriod}
                  onExceedsChange={props.onCandleBudgetExceedsChange}
                />
              </div>
            ) : null}
            {props.candleTimeframePreviewUi &&
            !props.candleTimeframePreviewUi.isLoading &&
            !props.candleTimeframePreviewUi.exceedsBudget &&
            !props.candleBudgetExceeds ? (
              <p
                className={cn(
                  "mt-2 text-xs font-[family-name:var(--font-primary)]",
                  props.candleTimeframePreviewUi.isWarning
                    ? "text-[color:var(--brand-warning-orange,#f59e0b)]"
                    : "text-muted-foreground"
                )}
              >
                {props.candleTimeframePreviewUi.primary}
              </p>
            ) : null}
            {props.marketDataUnavailable && !props.marketDataConnectGate ? (
              <BoxAny mt={3} w="full">
                <MarketDataUnavailablePanel
                  reason={props.marketDataUnavailable.reason}
                  message={props.marketDataUnavailable.message}
                  suggestedCandleTypes={props.marketDataUnavailable.suggestedCandleTypes}
                  earliestAvailable={props.marketDataUnavailable.earliestAvailable}
                  maxCandles={props.marketDataUnavailable.maxCandles}
                  limitBasis={props.marketDataUnavailable.limitBasis}
                  currentCandleType={props.candleTimeframe}
                  connectOptions={props.marketDataUnavailable.connectOptions}
                  tradeAnalyticsAvailable={props.marketDataUnavailable.tradeAnalyticsAvailable}
                  onPickCandleType={props.onMarketDataUnavailablePickCandleType}
                  onChangeDataSource={props.onMarketDataUnavailableChangeSource}
                  onConnectBroker={props.onMarketDataUnavailableConnectBroker}
                />
              </BoxAny>
            ) : null}
          </GenerateReportWizardFieldBlock>
        </SimpleGridAny>
      ) : null}

      {activeStepKey === "strategy" ? (
        <SimpleGridAny
          w="100%"
          columns={
            isDialog
              ? { base: 1, sm: props.strategyMode === "none" ? 1 : 2 }
              : props.strategyMode === "new"
                ? { base: 1, md: 3 }
                : props.strategyMode === "existing"
                  ? { base: 1, md: 2 }
                  : { base: 1, md: 1 }
          }
          spacing={isDialog ? 5 : 8}
        >
          <GenerateReportWizardFieldBlock
            label="Strategy mode"
            description={
              props.strategyStepDescription ||
              "Attach a saved strategy, create one inline, or run without a strategy."
            }
          >
            <GenerateReportWizardSelect
              value={props.strategyMode}
              onValueChange={(v) => props.onStrategyModeChange(v as GenerateReportStrategyMode)}
              options={[
                { value: "none", label: "No strategy" },
                { value: "existing", label: "Use existing strategy" },
              ]}
            />
          </GenerateReportWizardFieldBlock>

          {props.strategyMode === "existing" ? (
            <GenerateReportWizardFieldBlock
              label="Existing strategy"
              description="Pick a saved strategy for this report."
            >
              <GenerateReportWizardSelect
                value={props.selectedStrategyId}
                onValueChange={props.onSelectedStrategyIdChange}
                disabled={props.strategiesLoading || props.strategyOptions.length === 0}
                placeholder={
                  props.strategiesLoading
                    ? "Loading strategies…"
                    : props.strategyOptions.length === 0
                      ? "No strategies"
                      : "Choose strategy"
                }
                options={props.strategyOptions.map((opt) => ({ value: opt.id, label: opt.name }))}
              />
            </GenerateReportWizardFieldBlock>
          ) : null}

          {props.strategyMode === "new" ? (
            <>
              <GenerateReportWizardFieldBlock
                label="Strategy name"
                description={`Optional label for this strategy (${props.strategyName.length}/${props.strategyNameLimit}).`}
              >
                <Input
                  value={props.strategyName}
                  onChange={(e) => props.onStrategyNameChange(e.target.value)}
                  placeholder="e.g. NY Reversal Setup"
                  maxLength={props.strategyNameLimit}
                  className="text-gray-500"
                />
              </GenerateReportWizardFieldBlock>
              <GenerateReportWizardFieldBlock
                label="Strategy description"
                description={`Describe your setup, rules, and constraints (${props.strategyDescription.length}/${props.strategyDescriptionLimit}).`}
              >
                <ChakraTextareaAny
                  {...textareaProps}
                  w="100%"
                  value={props.strategyDescription}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    props.onStrategyDescriptionChange(e.target.value)
                  }
                  placeholder="Describe your strategy setup, rules, and constraints…"
                  resize="vertical"
                  minH="120px"
                  h="auto"
                  borderRadius={menuRadius}
                />
              </GenerateReportWizardFieldBlock>
            </>
          ) : null}
        </SimpleGridAny>
      ) : null}

      {activeStepKey === "generate" ? (
        <VStackAny align="stretch" spacing={isDialog ? 5 : 8} w="100%" data-generate-report-wizard-fields="">
          <Field className="w-full">
            <FieldLabel className={GENERATE_REPORT_WIZARD_FIELD_LABEL_CLASS}>Review</FieldLabel>
            <FieldDescription className={GENERATE_REPORT_WIZARD_FIELD_DESC_CLASS}>
              Confirm your choices below, then generate the report.
            </FieldDescription>
          </Field>
          <div data-shadcn-data-table="" className="w-full min-w-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[38%] whitespace-nowrap text-[color:var(--brand-tropical-surf)]">
                    Setting
                  </TableHead>
                  <TableHead className="text-[color:var(--brand-tropical-surf)]">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(
                  [
                    ["Trade side", tradeSideLabel],
                    ["Date range", props.windowSummaryLabel],
                    ["Broker account", props.effectiveBrokerAccountCode || "—"],
                    ...(showSymbolScopeFields
                      ? ([[symbolScopeReviewLabel, instrumentReviewLabel]] as const)
                      : []),
                    ["Report name", props.reportTitle.trim() || reportTypeLabel],
                    ["Report type", reportTypeLabel],
                    [
                      "Timeframe",
                      props.candleBudgetExceeds || props.candleTimeframePreviewUi?.exceedsBudget
                        ? `${props.candleTimeframe} (over candle limit — pick a larger timeframe)`
                        : props.candleTimeframe || "Choose timeframe",
                    ],
                    ["Strategy", strategySummary],
                  ] as const
                ).map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell className="align-top whitespace-nowrap font-medium text-muted-foreground">
                      {label}
                    </TableCell>
                    <TableCell className="align-top whitespace-normal text-foreground">{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </VStackAny>
      ) : null}
    </>
  );

  const dialogFooterClassName =
    // Opaque muted surface so height transitions never flash popover through the footer.
    "mx-0 mb-0 mt-0 w-full shrink-0 gap-0 rounded-b-[6px] border-t border-border bg-muted p-0 sm:flex-col";

  const footerActionRow = (
    <div className="flex w-full flex-wrap items-center justify-end gap-3 px-6 py-4">
      {activeStepIndex > 0 ? (
        <Button
          type="button"
          variant="outline-gray"
          size="sm"
          data-font="primary"
          className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
          onClick={goBack}
        >
          <span data-font="primary" className="truncate normal-case tracking-normal">
            Back
          </span>
        </Button>
      ) : null}
      {activeStepIndex < lastStepIndex ? (
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
          onClick={goNext}
          disabled={!canAdvance}
        >
          <span className="truncate normal-case tracking-normal">Continue</span>
        </Button>
      ) : (
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
          onClick={props.onGenerate}
          disabled={
            !props.canGenerate ||
            props.generating ||
            props.inflightCheckLoading ||
            props.rateLimitCoolingDown ||
            props.weeklyReportQuotaReached
          }
        >
          <span className="truncate normal-case tracking-normal">
            {props.generating
              ? "Generating..."
              : props.rateLimitCoolingDown
                ? `Wait ${props.rateLimitRetryAfterSeconds ?? 0}s`
                : "Generate report"}
          </span>
        </Button>
      )}
    </div>
  );

  const wizardFooter = isDialog ? (
    <DialogFooter
      data-generate-report-wizard-footer=""
      showCloseButton={false}
      className={cn(dialogFooterClassName, "flex-col items-stretch sm:flex-col")}
    >
      {footerAlertMessage ? (
        <div
          data-generate-report-wizard-footer-alert=""
          className="w-full border-b border-amber-500/25 bg-amber-500/10 px-6 py-3"
        >
          <SimulatorAlert
            tone="warning"
            className="w-full border-0 bg-transparent p-0 shadow-none"
          >
            {footerAlertMessage}
          </SimulatorAlert>
        </div>
      ) : null}
      {footerActionRow}
    </DialogFooter>
  ) : (
    <div
      data-generate-report-wizard-footer=""
      className="mt-4 flex w-full flex-col gap-3"
    >
      {footerAlertMessage ? (
        <SimulatorAlert tone="warning" className="w-full">
          {footerAlertMessage}
        </SimulatorAlert>
      ) : null}
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-font="primary"
        data-trade-log-action="delete"
        className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal border-destructive/40 text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive/60 focus-visible:ring-destructive/25 dark:border-destructive/50 dark:hover:bg-destructive/15"
        onClick={props.onCancel}
      >
        <span data-font="primary" className="truncate normal-case tracking-normal">
          Cancel
        </span>
      </Button>
      <HStackAny spacing={3}>
        {activeStepIndex > 0 ? (
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={goBack}
          >
            <span data-font="primary" className="truncate normal-case tracking-normal">
              Back
            </span>
          </Button>
        ) : null}
        {activeStepIndex < lastStepIndex ? (
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={goNext}
            disabled={!canAdvance}
          >
            <span className="truncate normal-case tracking-normal">Continue</span>
          </Button>
        ) : (
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={props.onGenerate}
            disabled={
              !props.canGenerate ||
              props.generating ||
              props.inflightCheckLoading ||
              props.rateLimitCoolingDown ||
              props.weeklyReportQuotaReached
            }
          >
            <span className="truncate normal-case tracking-normal">
              {props.generating
                ? "Generating..."
                : props.rateLimitCoolingDown
                  ? `Wait ${props.rateLimitRetryAfterSeconds ?? 0}s`
                  : "Generate report"}
            </span>
          </Button>
        )}
      </HStackAny>
      </div>
    </div>
  );

  const barCoverageFooter = (
    <DialogFooter
      data-generate-report-wizard-footer=""
      showCloseButton={false}
      className="mx-0 mb-0 mt-0 w-full shrink-0 justify-end gap-3 rounded-b-[6px] border-t border-border bg-muted px-6 py-4 sm:flex-row sm:justify-end"
    >
      <Button
        type="button"
        variant="outline-gray"
        size="sm"
        data-font="primary"
        className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
        onClick={closeBarCoverage}
      >
        <span data-font="primary" className="truncate normal-case tracking-normal">
          Back
        </span>
      </Button>
    </DialogFooter>
  );

  const dialogFooterSlot = useWorkspaceWizardFooterSlot();
  const dialogFooterPortal =
    isDialog && dialogFooterSlot
      ? createPortal(barCoverageOpen ? barCoverageFooter : wizardFooter, dialogFooterSlot)
      : null;

  const stepperAndSteps = (
    <GenerateReportWizardStepper
      activeStepIndex={activeStepIndex}
      onActiveStepIndexChange={setActiveStepIndex}
      stepCount={visibleSteps.length}
      compact={isDialog}
    >
      {visibleSteps.map((stepMeta, index) => (
        <GenerateReportWizardStepperContent key={stepMeta.key} step={index + 1}>
          {isDialog && stepMeta.key !== "generate" ? (
            <div className="mb-4">
              <p className="m-0 font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]">
                {stepMeta.label}
              </p>
              <p className="mt-1 m-0 font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white">
                {stepMeta.description}
              </p>
            </div>
          ) : null}
          {stepForm}
        </GenerateReportWizardStepperContent>
      ))}
    </GenerateReportWizardStepper>
  );

  if (isDialog) {
    const panelTransition = reduceMotion
      ? { duration: 0 }
      : DIALOG_PANEL_TRANSITION;

    return (
      <>
        {dialogFooterPortal}
        <GenerateReportWizardDialogBody
          accent={accent}
          barCoverageOpen={barCoverageOpen}
          reduceMotion={Boolean(reduceMotion)}
          panelTransition={panelTransition}
          hasInflightReport={props.hasInflightReport}
          weeklyReportQuotaReached={props.weeklyReportQuotaReached}
          weeklyReportQuotaMessage={props.weeklyReportQuotaMessage}
          stepperAndSteps={stepperAndSteps}
        />
      </>
    );
  }

  return (
    <FlexAny
      data-reports-page=""
      data-generate-report-wizard-layout="page"
      direction="column"
      minH={{ base: "calc(100vh - 100px)", md: "calc(100vh - 80px)", lg: "calc(100vh - 40px)" }}
      w="100%"
      sx={reportPageFieldFocusSx}
    >
      <BoxAny flex="1" w="100%" maxW="1120px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <ChakraButtonAny
          variant="ghost"
          h="auto"
          minH="32px"
          px={0}
          mb={6}
          color={props.subtleText}
          fontFamily="'IBM Plex Sans', sans-serif"
          fontSize="sm"
          fontWeight="medium"
          leftIcon={<Icon as={ArrowBackIcon} boxSize={4} />}
          onClick={props.onCancel}
          _hover={{ color: accent, bg: "transparent" }}
        >
          Back to reports
        </ChakraButtonAny>

        <BoxAny mb={{ base: 5, md: 6 }}>
          <TextAny
            fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
            color={accent}
            fontFamily="'Urbanist', sans-serif"
            fontWeight="normal"
            lineHeight="1.1"
            mb={2}
          >
            Create report
          </TextAny>
          <TextAny
            fontSize={{ base: "sm", md: "md" }}
            color={props.subtleText}
            fontFamily="'IBM Plex Sans', sans-serif"
            maxW="640px"
            lineHeight="1.5"
          >
            Walk through each step to define trades, report settings, and strategy.
          </TextAny>
        </BoxAny>

        {props.hasInflightReport ? (
          <AlertAny status="warning" borderRadius="12px" mb={4}>
            <AlertIconAny />
            <AlertDescriptionAny fontSize="sm">
              A report is already generating. Wait for it to finish before starting another.
            </AlertDescriptionAny>
          </AlertAny>
        ) : props.weeklyReportQuotaReached ? (
          <AlertAny status="warning" borderRadius="12px" mb={4}>
            <AlertIconAny />
            <AlertDescriptionAny fontSize="sm">
              {props.weeklyReportQuotaMessage ||
                "You've reached this week's report limit. Wait until next week, or upgrade to Plus for a higher cap."}
            </AlertDescriptionAny>
          </AlertAny>
        ) : null}

        <BoxAny
          data-reports-wizard-card=""
          bg={cardBg}
          borderWidth="1px"
          borderColor={props.borderColor}
          borderRadius="16px"
          p={{ base: 5, md: 8 }}
          boxShadow={cardShadow}
          w="100%"
          overflow="visible"
          sx={{
            _dark: {
              borderColor: "var(--border)",
            },
          }}
        >
          {stepperAndSteps}
        </BoxAny>

        {wizardFooter}
      </BoxAny>
    </FlexAny>
  );
}
